import axiosInstance from './axiosInstance';
import axiosV2Instance from './axiosV2Instance';

export interface QuestionV2 {
  id: number;
  userId: number;
  siteId: number;
  text: string;
  closed: boolean;
  visibilityType: number;
  createdAt?: string;
  user?: {
    id: number;
    name: string;
    closed: boolean;
  };
  site?: {
    id: number;
    name: string;
    closed: boolean;
  };
  userName?: string;
  siteName?: string;
  typeOfWorkId?: number;
  typeId?: number;
  typeOfWorks?: {
    id: number;
    name: string;
    closed: boolean;
  };
}

export interface QuestionRolePermission {
  // TODO: Define properties
}

export interface QuestionStatistics {
  total: number;
  open: number;
  closed: number;
  my: number;
}

export interface QuestionMember {
  id?: number;
  userId: number;
}

export interface AddMemberRequest {
  // TODO: Define properties
}

export interface CreateQuestionV2Request {
  userId: number;
  siteId: number;
  typeOfWorkId: number;
  typeId: number;
  text: string;
  closed: boolean;
  visibilityType?: number; // 1 = private, 2 = public
}

export interface UpdateQuestionV2Request {
  text: string;
  closed?: boolean;
  visibilityType?: number; // 1 = private, 2 = public
  typeOfWorkId?: number;
}

class QuestionsV2Api {
  async getAllQuestions(): Promise<QuestionV2[]> {
    try {
      // Deprecated: используйте getSiteQuestions(siteId) вместо этого метода
      console.warn('getAllQuestions is deprecated, use getSiteQuestions instead');
      const response = await axiosInstance.get<QuestionV2[]>('/Questions');
      return response.data;
    } catch (error) {
      console.error('Error fetching all questions:', error);
      throw error;
    }
  }

  /**
   * Получить все вопросы объекта без фильтрации по типу (deprecated - используйте getSiteQuestions)
   */
  async getSiteQuestionsAll(siteId: number): Promise<QuestionV2[]> {
    try {
      const response = await axiosV2Instance.get<QuestionV2[]>(`/sites/${siteId}/questions`);
      return response.data;
    } catch (error) {
      console.error('Error fetching site questions:', error);
      throw error;
    }
  }

  async getQuestion(siteId: number, id: number): Promise<QuestionV2> {
    try {
      const response = await axiosV2Instance.get<QuestionV2>(`/sites/${siteId}/questions/${id}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching question:', error);
      throw error;
    }
  }

  async createQuestion(siteId: number, questionData: CreateQuestionV2Request): Promise<{ id: number }> {
    try {
      const response = await axiosV2Instance.post<{ id: number }>(`/sites/${siteId}/questions`, questionData);
      return response.data;
    } catch (error) {
      console.error('Error creating question:', error);
      throw error;
    }
  }

  async updateQuestion(siteId: number, id: number, questionData: UpdateQuestionV2Request): Promise<void> {
    try {
      await axiosV2Instance.put(`/sites/${siteId}/questions/${id}`, questionData);
    } catch (error) {
      console.error('Error updating question:', error);
      throw error;
    }
  }

  async closeQuestion(siteId: number, id: number): Promise<void> {
    try {
      await axiosV2Instance.post(`/sites/${siteId}/questions/${id}/close`);
    } catch (error) {
      console.error('Error closing question:', error);
      throw error;
    }
  }

  async reopenQuestion(siteId: number, id: number): Promise<void> {
    try {
      await axiosV2Instance.post(`/sites/${siteId}/questions/${id}/reopen`);
    } catch (error) {
      console.error('Error reopening question:', error);
      throw error;
    }
  }

  async deleteQuestion(siteId: number, id: number): Promise<void> {
    try {
      await axiosV2Instance.delete(`/sites/${siteId}/questions/${id}`);
    } catch (error) {
      console.error('Error deleting question:', error);
      throw error;
    }
  }

  async addQuestionMember(siteId: number, questionId: number, userId: number): Promise<void> {
    try {
      await axiosV2Instance.post(`/sites/${siteId}/questions/${questionId}/members`, { userId });
    } catch (error) {
      console.error('Error adding question member:', error);
      throw error;
    }
  }

  async getQuestionMembers(siteId: number, questionId: number): Promise<QuestionMember[]> {
    try {
      const response = await axiosV2Instance.get<QuestionMember[]>(`/sites/${siteId}/questions/${questionId}/members`);
      return response.data;
    } catch (error) {
      console.error('Error getting question members:', error);
      throw error;
    }
  }

  async removeQuestionMember(siteId: number, questionId: number, userId: number): Promise<void> {
    try {
      await axiosV2Instance.delete(`/sites/${siteId}/questions/${questionId}/members/${userId}`);
    } catch (error) {
      console.error('Error removing question member:', error);
      throw error;
    }
  }

  /** Права участника чата: получить список кодов прав для участника (userId) в вопросе/чате (questionId). */
  async getQuestionMemberPermissions(siteId: number, questionId: number, userId: number): Promise<string[]> {
    const response = await axiosV2Instance.get<{ permissionCodes: string[] }>(
      `/sites/${siteId}/questions/${questionId}/members/${userId}/permissions`
    );
    return response.data.permissionCodes ?? [];
  }

  /** Права участника чата: установить список кодов прав для участника (userId) в вопросе/чате (questionId). */
  async updateQuestionMemberPermissions(
    siteId: number,
    questionId: number,
    userId: number,
    permissionCodes: string[]
  ): Promise<void> {
    await axiosV2Instance.put(
      `/sites/${siteId}/questions/${questionId}/members/${userId}/permissions`,
      { permissionCodes }
    );
  }

  /**
   * Универсальный метод для получения контента по типу
   * @param siteId ID объекта
   * @param typeId Тип контента: 1 = вопросы, 2 = заметки, 3 = снабжение
   */
  async getSiteContentByTypeV2(siteId: number, typeId: number): Promise<QuestionV2[]> {
    try {
      // Используем endpoint с route параметром для надежной передачи typeId
      const response = await axiosV2Instance.get<QuestionV2[]>(`/sites/${siteId}/questions/type/${typeId}`);
      console.log(`Loaded content for site ${siteId} with typeId ${typeId}:`, response.data.length, 'items');
      
      // Дополнительная проверка на фронтенде - фильтруем по typeId
      const filteredData = response.data.filter(item => item.typeId === typeId);
      
      if (filteredData.length !== response.data.length) {
        console.warn(`TypeId mismatch! Expected ${typeId}, got items with different typeIds. Filtered from ${response.data.length} to ${filteredData.length}`);
      }
      
      return filteredData;
    } catch (error) {
      console.error('Error fetching site content by type:', error);
      throw error;
    }
  }

  async editQuestionText(siteId: number, questionId: number, newText: string): Promise<void> {
    // TODO: implement
  }

  async getAssignableRoles(siteId: number): Promise<any[]> {
    // TODO: implement
    return [];
  }
}

export const questionsV2Api = new QuestionsV2Api();
